import React from 'react';
import T from 'prop-types';
import {
  Navigation,
  Form,
  Fieldset,
  FieldFeedback,
  Legend,
  YesNoRadio,
} from '@piggybank/form';
import {
  Heading,
  Panel,
  Button,
  HorizontalRule,
  Reveal,
} from '@piggybank/core';

import { isEmpty, get } from 'lodash-es';
import hooks from 'hooksAlias';
import { Translation, TranslationScope } from 'srcAlias/client/locale';
import useTrackPageViewAnalytics from 'srcAlias/client/global/customHooks/useTrackPageViewAnalytics';
import { callTrackEventAnalyticsFunction } from 'srcAlias/client/global/customHooks/useTrackEventAnalytics';
import {
  selectProductCode,
  selectQueryParams,
} from 'srcAlias/client/state/selectors';
import { useSelector } from 'react-redux';
import { ProductDetailsList } from './ProductDetailsList';
import { OtherEquityMarketCountries } from './OtherEquityMarketCountries';
import { redirectionToCKA } from 'srcAlias/client/utils/redirectionToCKA';
import translations from 'contentAlias';
import config from 'configAlias';
export const Products = ({
  segmentName,
  pageName,
  eligibleProducts,
  hasEquitiesProduct,
}) => {
  const productID = useSelector(selectProductCode); //IN1 --> UT_HBSP
  useTrackPageViewAnalytics('products');
  const queryParams = useSelector(selectQueryParams);
  const HorizontalRuleHook = get(
    hooks,
    'horizontalRule.HorizontalRuleHook',
    () => null
  );
  const resetNextPageValues = get(
    hooks,
    'validations.default.resetValues.resetNextPageValues',
    null
  );
  const validateData = (values) => {
    const invalidData = {};
    if (!isEmpty(eligibleProducts) && values.otherMarkets === '') {
      invalidData.otherMarkets = 'Please select yes or no';
    }
    if (
      !isEmpty(eligibleProducts) &&
      (values.otherMarkets === 'no' || !values.otherMarkets)
    ) {
      if (!values.product) {
        invalidData.product = translations.customer.products.validationMsg;
      }
    } else {
      if (values.markets.HK === false && values.markets.US === false) {
        invalidData.markets = 'Please select at least one market';
      }
    }

    return invalidData;
  };
  const initialValuesForm = {
    product: productID ? productID : '',
  };

  if (hasEquitiesProduct) {
    initialValuesForm.otherMarkets = isEmpty(eligibleProducts) ? '' : 'no';
    initialValuesForm.markets = {
      US: false,
      HK: false,
    };
  }

  const continueButtonClick = (values, event, page, params) => {
    if (values.otherMarkets === 'yes') {
      callTrackEventAnalyticsFunction(
        'onChange',
        'otherMarketsYesNoButtonClick',
        values
      );
    }
    if (values.markets.HK === true) {
      callTrackEventAnalyticsFunction(
        'onChange',
        'otherMarketsHKCheckboxClick',
        values
      );
    }
    if (values.markets.US === true) {
      callTrackEventAnalyticsFunction(
        'onChange',
        'otherMarketsUSCheckboxClick',
        values
      );
    }

    callTrackEventAnalyticsFunction(
      'onClick',
      'continueButtonClickFromProductPage',
      {
        otherMarkets: 'no',
      }
    );

    redirectionToCKA(values, event, page, params);
  };

  return (
    <TranslationScope path={`${segmentName}.${pageName}`}>
      <Heading level={1}>
        <Translation path="heading" />
      </Heading>
      <HorizontalRule />
      <Form
        initialValues={initialValuesForm}
        name={'productDetails'}
        validate={(values) => validateData(values)}
        onSubmit={
          resetNextPageValues
            ? (next, rest) => resetNextPageValues(next, rest)
            : undefined
        }
      >
        {({ values }) => (
          <div>
            {hasEquitiesProduct && (
              <div>
                <Heading level={2} accentBar>
                  <Translation path="hasEquityAccountHeading" />
                </Heading>
                <Panel>
                  <Heading level={3} marginBottom={0}>
                    <Translation path="hasEquityAccountPanelLabel" />
                  </Heading>
                </Panel>
                {!isEmpty(eligibleProducts) && (
                  <Fieldset marginBottom={2} name="otherMarkets">
                    <Legend>
                      <Translation path="otherMarketsRadio" />
                    </Legend>
                    <YesNoRadio />
                    <FieldFeedback />
                  </Fieldset>
                )}

                <Reveal accentBar={!isEmpty(eligibleProducts)}>
                  {(values.otherMarkets === 'yes' ||
                    isEmpty(eligibleProducts)) && (
                    <OtherEquityMarketCountries />
                  )}
                </Reveal>
              </div>
            )}
            <div>
              {(!hasEquitiesProduct || values.otherMarkets === 'no') && (
                <ProductDetailsList
                  eligibleProducts={eligibleProducts}
                  hasEquitiesProduct={hasEquitiesProduct}
                />
              )}
            </div>
            <HorizontalRuleHook />

            <Navigation
              nextSlot={(() => {
                if (
                  (values.otherMarkets === 'yes' ||
                    isEmpty(eligibleProducts)) &&
                  (values.markets.HK || values.markets.US)
                ) {
                  return (
                    <Button
                      onClick={(event) =>
                        continueButtonClick(
                          values,
                          event,
                          pageName,
                          queryParams
                        )
                      }
                    >
                      <Translation path={'startInvestingButton'} />
                    </Button>
                  );
                }
                if (config.continueButtonOnProductionSelection === true) {
                  return (
                    <Button name={'productDetailsContinue'}>Continue</Button>
                  );
                }
                return undefined;
              })()}
            />
          </div>
        )}
      </Form>
    </TranslationScope>
  );
};

Products.propTypes = {
  segmentName: T.string.isRequired,
  pageName: T.string.isRequired,
  products: T.object,
  eligibleProducts: T.array,
  productID: T.string,
  hasEquitiesProduct: T.bool,
};

export default Products;
